import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/cn'
import { Spinner } from '@/components/ui/Spinner'
import { KpiCard } from '@/components/dashboard/KpiCard'
import { SenegalMap } from '@/components/dashboard/SenegalMap'
import { RegionPanel } from '@/components/dashboard/RegionPanel'
import { OrientationHistoryRow } from '@/components/dashboard/OrientationHistoryRow'
import { dashboardApi } from '@/lib/dashboardApi'
import type { NationalDashboard, RegionDashboard } from '@/lib/dashboardApi'
import { useDashboardWebSocket } from '@/hooks/useDashboardWebSocket'

const REFETCH_INTERVAL_MS = 30_000

/* ── WebSocket status dot ────────────────────────────────────────── */

interface WsStatusDotProps {
  connected: boolean
}

function WsStatusDot({ connected }: WsStatusDotProps) {
  return (
    <div className="flex items-center gap-1.5" title={connected ? 'WebSocket connecté' : 'WebSocket déconnecté'}>
      <span
        className={cn(
          'inline-block w-2 h-2 rounded-full',
          connected ? 'bg-success-500' : 'bg-neutral-300',
        )}
      />
      <span className="text-xs text-neutral-400 hidden sm:inline">
        {connected ? 'En direct' : 'Déconnecté'}
      </span>
    </div>
  )
}

/* ── DashboardPage ───────────────────────────────────────────────── */

export default function DashboardPage() {
  const queryClient = useQueryClient()
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null)

  // ── WebSocket: invalidate dashboard queries on each push update ──
  const handleWsUpdate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }, [queryClient])

  const { connected } = useDashboardWebSocket({ onUpdate: handleWsUpdate })

  // ── Primary query: national dashboard, polled every 30s ─────────
  const {
    data: national,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery<NationalDashboard>({
    queryKey: ['dashboard', 'national'],
    queryFn: () => dashboardApi.getNational(),
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: REFETCH_INTERVAL_MS,
  })

  // ── Secondary query: region detail, only when a region is selected
  const {
    data: regionDashboard,
    isLoading: isRegionLoading,
  } = useQuery<RegionDashboard>({
    queryKey: ['dashboard', 'region', selectedRegionId],
    queryFn: () => dashboardApi.getRegion(selectedRegionId!),
    enabled: selectedRegionId !== null,
    staleTime: REFETCH_INTERVAL_MS,
  })

  // ── Handlers ─────────────────────────────────────────────────────
  const handleRegionClick = useCallback((regionId: string) => {
    setSelectedRegionId((prev) => (prev === regionId ? null : regionId))
  }, [])

  const handlePanelClose = useCallback(() => {
    setSelectedRegionId(null)
  }, [])

  const handleManualRefresh = useCallback(() => {
    void refetch()
  }, [refetch])

  // ── Derived data ─────────────────────────────────────────────────
  const indicators = national?.indicators
  const recentOrientations = (national?.recentOrientations ?? []).slice(0, 10)

  // ── Render: loading skeleton ──────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p className="text-sm text-neutral-500">Chargement du tableau de bord...</p>
        </div>
      </div>
    )
  }

  // ── Render: error state ───────────────────────────────────────────
  if (isError || !national) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3 text-center px-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-12 h-12 text-error-300"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-sm font-medium text-neutral-700">
            Impossible de charger le tableau de bord national
          </p>
          <button
            onClick={handleManualRefresh}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium underline underline-offset-2"
          >
            Réessayer
          </button>
        </div>
      </div>
    )
  }

  // ── Render: main layout ───────────────────────────────────────────
  return (
    <div className="p-4 lg:p-6 space-y-5">
      {/* ── Page header ─────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-neutral-900">Dashboard National</h1>
          <p className="text-xs text-neutral-400 mt-0.5">
            Mis à jour&nbsp;
            {new Date(national.generatedAt).toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <WsStatusDot connected={connected} />
          <button
            onClick={handleManualRefresh}
            disabled={isFetching}
            className={cn(
              'flex items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-3 py-1.5',
              'text-xs font-medium text-neutral-700 shadow-sm',
              'hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500',
              'disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150',
            )}
            title="Rafraîchir maintenant (auto toutes les 30s)"
          >
            {isFetching ? (
              <Spinner size="sm" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-3.5 h-3.5"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M15.312 3.312a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-.75.75h-3.75a.75.75 0 0 1 0-1.5h1.978A6.25 6.25 0 1 0 10 16.25a.75.75 0 0 1 0 1.5A7.75 7.75 0 1 1 17.75 10a.75.75 0 0 1-1.5 0 6.265 6.265 0 0 0-.91-3.262H15.312Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span className="hidden sm:inline">30s</span>
          </button>
        </div>
      </div>

      {/* ── KPI cards row ────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard
          label="Structures"
          value={indicators?.totalStructures ?? 0}
        />
        <KpiCard
          label="Connectées 24h"
          value={indicators?.connectedLast24h ?? 0}
          unit="24h"
        />
        <KpiCard
          label="Lits totaux"
          value={indicators?.totalBeds ?? 0}
        />
        <KpiCard
          label="Lits occupés"
          value={
            indicators
              ? `${indicators.occupiedBeds} (${(indicators.occupancyRate * 100).toFixed(1)}%)`
              : 0
          }
        />
        <KpiCard
          label="Alertes stock"
          value={indicators?.stockAlerts ?? 0}
          alert={(indicators?.stockAlerts ?? 0) > 0}
        />
      </div>

      {/* ── Map + Region panel row ───────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Map: 60% (3/5 cols) */}
        <div className="lg:col-span-3">
          <SenegalMap
            regions={national.regions}
            selectedRegionId={selectedRegionId}
            onRegionClick={handleRegionClick}
          />
        </div>

        {/* Region detail panel: 40% (2/5 cols) */}
        <div className="lg:col-span-2">
          <RegionPanel
            regionDashboard={regionDashboard ?? null}
            loading={isRegionLoading}
            onClose={handlePanelClose}
          />
        </div>
      </div>

      {/* ── Recent orientations table ────────────────────────── */}
      <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
        {/* Table header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
          <h2 className="text-sm font-semibold text-neutral-800">
            Dernières orientations
          </h2>
          <span className="text-xs text-neutral-400">
            {recentOrientations.length} entrée{recentOrientations.length !== 1 ? 's' : ''}
          </span>
        </div>

        {recentOrientations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-8 h-8 text-neutral-200"
              aria-hidden="true"
            >
              <path d="M9 12h6m-3-3v6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <p className="text-sm text-neutral-400">Aucune orientation récente</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-100 bg-neutral-50">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide whitespace-nowrap">
                    Référence
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">
                    Motif
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide whitespace-nowrap">
                    Gravité
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide whitespace-nowrap">
                    Statut
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">
                    Structure choisie
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-neutral-500 uppercase tracking-wide whitespace-nowrap">
                    Heure
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentOrientations.map((entry) => (
                  <OrientationHistoryRow key={entry.orientationRef} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
